<template>
  <div class="share-cart">
    <SvgIcon
      :image="SharCart"
      class="share-cart__icon"
    />
    <div class="share-cart__information">
      <strong>Warenkorb verschicken</strong><br />
      Sende den Inhalt des Warenkorbes an eine E-Mail-Adresse
      <template v-if="isShareSupported">oder teile den Warenkorb</template>
      <template v-else>oder kopiere den Link in die Zwischenablage</template>.
    </div>

    <form
      v-if="!emailSent"
      class="share-cart__form"
      @submit.prevent="sendForm"
    >
      <TextInput
        v-model="email"
        label="E-Mail-Adresse eingeben"
        inline-label
        id-prefix="email"
        type="email"
        class="share-cart__form-input"
      />

      <LinkButton
        :disabled="!isValidMail"
        tag="button"
        type="submit"
        primary
      >
        Absenden
      </LinkButton>
    </form>

    <LinkButton
      v-if="isShareSupported || isCopySupported"
      tag="button"
      class="share-cart__share"
      outline
      @click="share"
    >
      <template v-if="isShareSupported">Warenkorb teilen</template>
      <template v-else>Warenkorblink kopieren</template>
    </LinkButton>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useNuxtApp, useRuntimeConfig } from '#imports';
import ShopApi from '@/lib/shop-api';
import SharCart from '@/assets/images/share-cart.svg';
import Dialog from '@/helpers/dialog';

import { LinkButton, TextInput } from '@i22-td-smarthome/component-library';

import type { Item } from '@/lib/types/cartdata';

const store = useNuxtApp().$store;

interface ShareData {
  files?: File[];
  text?: string;
  title: string;
  url: string;
}

const config = useRuntimeConfig();

const shareData = computed<ShareData>(() => {
  const url = new URL('/shop/warenkorb', config.public.baseUrl);
  store.getters['cart/cartItems'].forEach((item: Item) => {
    for (let i = 0; i < item.quantity; i += 1) {
      url.searchParams.append('articleNumbers[]', item.articleNumber);
    }
  });

  return {
    title: 'Mein Telekom Warenkorb',
    url: url.toString(),
  };
});

const isShareSupported = computed(
  () => typeof navigator.canShare !== 'undefined' && navigator.canShare(shareData.value)
);
const isCopySupported = computed(() => typeof navigator.clipboard?.writeText === 'function');

const email = ref<string>('');
const emailSent = ref(false);
const isValidMail = computed<boolean>(() => /(.*)@(.*)\.(.*)/.test(email.value));

const share = async () => {
  if (isShareSupported.value) {
    navigator
      .share(shareData.value)
      .then(() => {})
      .catch(() => {});

    return;
  }

  if (isCopySupported.value) {
    navigator.clipboard?.writeText(shareData.value.url).then(
      () => {
        Dialog.info(`Der Warenkorb-Link wurde in die Zwischenablage kopiert.`, {
          type: 'success',
        });
      },
      () => {
        console.error('Failed to copy');
      }
    );
  }
};

const sendForm = async () => {
  try {
    const response = await ShopApi.shareCart(email.value);
    if (response.status === 'Cart send.') {
      emailSent.value = true;
      Dialog.info(`Der Warenkorb wurde erfolgreich an folgende E-Mail versendet: ${email.value}.`, {
        type: 'success',
      });
    } else {
      throw new Error(response.status);
    }
  } catch (e: Error | any) {
    console.error(e);
  }
};
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.share-cart {
  margin-bottom: $grid-base * 3;
  display: grid;
  grid-template-areas: 'icon text' 'form form' 'share share';
  grid-template-columns: 88px auto;
  grid-gap: $grid-base;
  border-block: 1px solid color(grey, 25000);
  padding-block: $grid-base * 2;

  @include mq(false, $mq-md - 1) {
    max-width: 600px;
    margin-inline: auto;
  }
  @include mq($mq-md) {
    grid-template-areas: 'icon text form' 'icon text share';
    grid-template-columns: 88px 1fr 1fr;
  }

  &__information {
    grid-area: text;
  }

  &__icon {
    grid-area: icon;
    width: 88px;
    height: 77px;
    float: left;
    margin-right: $grid-base;
  }

  &__share {
    grid-area: share;
  }

  &__form {
    grid-area: form;
    display: flex;
    grid-gap: $grid-base;
    align-items: flex-end;

    &-input {
      flex: 1 1 100%;
    }
  }
}
</style>
